import {
  PDFArray,
  PDFDict,
  PDFDocument,
  PDFName,
  PDFNull,
  PDFNumber,
  PDFPageLeaf,
  PDFRef,
  PDFString,
} from "pdf-lib";

export interface PDFOutlineNode {
  /** The label for this bookmark. */
  label: string;
  /** Hex string. Must be in 6-character format, e.g. "#FFFFFF" / "808080". */
  color?: string;
  /** If the bookmark should be bold. */
  bold?: boolean;
  /** If the bookmark should be italic. */
  italic?: boolean;
  /** The page to link to. */
  page?: PDFRef | number;
  /** Where on the page to jump to. */
  fit?:
    | "fitPage"
    | "fitHorizontal"
    | "fitVertical"
    | {
        type: "coordinate";
        x: number | null;
        y: number | null;
        zoom: number | null;
      }
    | {
        type: "box";
        left: number;
        right: number;
        top: number;
        bottom: number;
      };
  /** Child bookmarks of this bookmark. */
  children?: PDFOutlineNode[];
}

export class PDFService {
  /** Generate a PDFDocument from an array buffer. */
  async pdfFromArrayBuffer(buffer: ArrayBuffer): Promise<PDFDocument> {
    return await PDFDocument.load(buffer);
  }

  /** Generate a PDFDocument from a blob. */
  async pdfFromBlob(blob: Blob): Promise<PDFDocument> {
    return await blob.arrayBuffer().then((buffer) => PDFDocument.load(buffer));
  }

  /** Get an array of refs for all pages in the PDF, indexed by the page number. */
  getPageRefs(pdf: PDFDocument) {
    const refs: PDFRef[] = [];
    pdf.catalog.Pages().traverse((kid, ref) => {
      if (kid instanceof PDFPageLeaf) refs.push(ref);
    });
    return refs;
  }

  /** Build an outline PDF object for the given PDF.
   *
   * Note: This method does not automatically attach the outline to the PDF's catalog. To do
   * this, simply use the following code:
   *
   * ```ts
   * const pdfOutline = pdfService.buildOutline(pdf, {...});
   * pdf.catalog.set(PDFName.of("Outlines"), pdfOutline);
   * ``` */
  buildOutline(pdf: PDFDocument, outline: PDFOutlineNode[]) {
    // Refer to ISO 32000-2 Section 12.3.2 for details on this implementation:

    let pageRefs: PDFRef[] | null = null;
    const pageRef = (index: PDFRef | number) => {
      if (index instanceof PDFRef) return index;
      pageRefs ??= this.getPageRefs(pdf);
      return pageRefs[index];
    };
    const parseColor = (color: string) => {
      const m = color.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/);
      const [r, g, b] = m ? m.slice(1) : ["00", "00", "00"];

      const pdfColor = PDFArray.withContext(pdf.context);
      pdfColor.push(PDFNumber.of(parseInt(r, 16) / 255));
      pdfColor.push(PDFNumber.of(parseInt(g, 16) / 255));
      pdfColor.push(PDFNumber.of(parseInt(b, 16) / 255));
      return pdfColor;
    };

    /** Create an outline item. */
    const createOutlineItem = (
      node: PDFOutlineNode,
      parent: PDFRef
    ): { item: PDFDict; ref: PDFRef } => {
      let dest: PDFArray | null = null;
      if (node.page != null) {
        const ref = pageRef(node.page);

        dest = PDFArray.withContext(pdf.context);
        dest.push(ref);

        if (node.fit === "fitPage") {
          dest.push(PDFName.of("Fit"));
        } else if (node.fit === "fitHorizontal") {
          dest.push(PDFName.of("FitH"));
          dest.push(PDFNull);
        } else if (node.fit === "fitVertical") {
          dest.push(PDFName.of("FitV"));
          dest.push(PDFNull);
        } else if (node.fit?.type === "coordinate") {
          dest.push(PDFName.of("XYZ"));
          dest.push(node.fit.x == null ? PDFNull : PDFNumber.of(node.fit.x));
          dest.push(node.fit.y == null ? PDFNull : PDFNumber.of(node.fit.y));
          dest.push(
            node.fit.zoom == null ? PDFNull : PDFNumber.of(node.fit.zoom)
          );
        } else if (node.fit?.type === "box") {
          dest.push(PDFName.of("FitR"));
          dest.push(PDFNumber.of(node.fit.left));
          dest.push(PDFNumber.of(node.fit.bottom));
          dest.push(PDFNumber.of(node.fit.right));
          dest.push(PDFNumber.of(node.fit.top));
        } else {
          dest.push(PDFName.of("Fit"));
        }
      }

      const pdfItemRef = pdf.context.nextRef();
      const pdfItem = PDFDict.withContext(pdf.context);
      pdf.context.assign(pdfItemRef, pdfItem);

      pdfItem.set(PDFName.of("Title"), PDFString.of(node.label));
      if (node.color) pdfItem.set(PDFName.of("C"), parseColor(node.color));
      if (dest) pdfItem.set(PDFName.of("Dest"), dest);
      pdfItem.set(PDFName.of("Parent"), parent);

      if (node.children?.length) {
        const children = processNodeArray(node.children, pdfItemRef);
        if (children.first) pdfItem.set(PDFName.of("First"), children.first);
        if (children.last) pdfItem.set(PDFName.of("Last"), children.last);
      }

      if (node.bold != null || node.italic != null) {
        const flags = (node.italic ? 1 : 0) | (node.bold ? 2 : 0);
        pdfItem.set(PDFName.of("F"), PDFNumber.of(flags));
      }

      return { item: pdfItem, ref: pdfItemRef };
    };

    /** Create an array of outline items. */
    const processNodeArray = (
      array: PDFOutlineNode[],
      parent: PDFRef
    ): { items: PDFDict[]; first: PDFRef | null; last: PDFRef | null } => {
      const items: PDFDict[] = [];
      let first: PDFRef | null = null;
      let last: PDFRef | null = null;

      let prev: { item: PDFDict; ref: PDFRef } | null = null;
      for (const node of array) {
        const item = createOutlineItem(node, parent);
        items.push(item.item);

        first ??= item.ref;
        last = item.ref;

        // link up list
        if (prev) {
          prev.item.set(PDFName.of("Next"), item.ref);
          item.item.set(PDFName.of("Prev"), prev.ref);
        }
        prev = item;
      }

      return { items, first, last };
    };

    // create the outline dict
    const pdfOutlineRef = pdf.context.nextRef();
    const pdfOutline = PDFDict.withContext(pdf.context);
    pdf.context.assign(pdfOutlineRef, pdfOutline);

    pdfOutline.set(PDFName.of("Type"), PDFName.of("Outlines"));

    const children = processNodeArray(outline, pdfOutlineRef);
    if (children.first) pdfOutline.set(PDFName.of("First"), children.first);
    if (children.last) pdfOutline.set(PDFName.of("Last"), children.last);
    if (children.items.length) {
      pdfOutline.set(PDFName.of("Count"), PDFNumber.of(children.items.length));
    }
    return pdfOutline;
  }

  /** Merge an array of PDFs into one combined PDF. Include the `label` field for one or more
   * PDFs to add an outline linking those pages by the given name. */
  async mergePDFs(
    pdfs: { pdf: PDFDocument; label?: string }[]
  ): Promise<PDFDocument> {
    const merged = await PDFDocument.create();
    const bookmarks: { page: number; label: string }[] = [];

    // generate combined document
    let page = 0;
    for (const { pdf, label } of pdfs) {
      const pages = await merged.copyPages(pdf, pdf.getPageIndices());
      if (label) {
        bookmarks.push({ page, label });
      }
      for (const page of pages) {
        merged.addPage(page);
      }
      page += pages.length;
    }

    // generate and attach outline from bookmarks
    if (bookmarks.length) {
      const outlines = this.buildOutline(merged, bookmarks);
      merged.catalog.set(PDFName.of("Outlines"), outlines);
    }

    return merged;
  }

  /** Get an array buffer from the PDF data. */
  async toBuffer(pdf: PDFDocument): Promise<Uint8Array> {
    return await pdf.save();
  }

  /** Get a blob from the PDF data. */
  async toBlob(pdf: PDFDocument): Promise<Blob> {
    const buffer = await pdf.save();
    return new Blob([buffer as BlobPart], { type: "application/pdf" });
  }
}

export const pdfService = new PDFService();
